import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import type { Database } from '../lib/database.types';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { RealtimeChannel } from '@supabase/supabase-js';
import { useUser } from './UserContext';
import { uniqueChannelName } from '../lib/realtime';
import { onAppRefresh } from '../lib/appRefresh';

export type ClientStatus = 'Active' | 'Prospect' | 'Paused' | 'Archived';

export interface ClientPlatformId {
  id: string;
  platform: string;
  identifier: string;
  url: string | null;
}

export interface ClientNote {
  id: string;
  content: string;
  createdAt: string;
  createdBy: {
    id: string;
    name: string;
  };
}

export interface ClientFile {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadedBy: {
    id: string;
    name: string;
  };
  uploadedAt: string;
  url: string;
}

export interface ClientTask {
  id: string;
  title: string;
  status: string;
  dueDate: string | null;
  assignedBy: string | null;
  website_id?: string;
  website_title?: string;
}

export interface Client {
  id: string;
  name: string;
  status: ClientStatus;
  email?: string;
  phone?: string;
  industry?: string;
  notes?: string;
  tags: string[];
  accountOwner?: {
    id: string;
    name: string;
  };
  startDate?: string;
  lastActivityDate?: string;
  nextMeetingDate?: string;
  media_domain?: string;
  google_sheet_url?: string;
  createdAt: string;
  updatedAt: string;
  platforms?: ClientPlatformId[];
  clientNotes?: ClientNote[];
  files?: ClientFile[];
  tasks?: ClientTask[];
  tasks_in_progress: number;
  tasksDueThisWeek: number;
  tasksDueToday: number;
  tasksAwaitingReview: number;
}

interface Metrics {
  activeClients: number;
  newClientsThisMonth: number;
  lostClientsThisMonth: number;
  netChange: number;
}

interface ClientContextType {
  clients: Client[];
  setClients: React.Dispatch<React.SetStateAction<Client[]>>;
  statusFilter: ClientStatus | 'All';
  setStatusFilter: React.Dispatch<React.SetStateAction<ClientStatus | 'All'>>;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  filteredClients: Client[];
  selectedClient: Client | null;
  setSelectedClient: React.Dispatch<React.SetStateAction<Client | null>>;
  isLoading: boolean;
  metrics: Metrics;
  fetchClients: () => Promise<void>;
  getClientById: (id: string) => Promise<Client | undefined>;
  addClientTask: (clientId: string, taskData: any) => Promise<{ success: boolean; error?: string }>;
  updateClientTaskStatus: (taskId: string, status: string) => Promise<boolean>;
  addClientPlatformId: (clientId: string, platformData: any) => Promise<boolean>;
  updateClientTags: (clientId: string, tags: string[]) => Promise<boolean>;
}

const defaultMetrics: Metrics = {
  activeClients: 0,
  newClientsThisMonth: 0,
  lostClientsThisMonth: 0,
  netChange: 0,
};

const ClientContext = createContext<ClientContextType | undefined>(undefined);

// Helper function to safely execute Supabase queries with retry logic
type ClientRow = Database['public']['Tables']['clients']['Row'];

const safeSupabaseQuery = async (queryFn: () => PromiseLike<any>, retries = 3, delay = 1000) => {
  let attempt = 0;
  let lastError;
  while (attempt < retries) {
    try {
      return await queryFn();
    } catch (error) {
      lastError = error;
      console.error(`Supabase query attempt ${attempt + 1} failed:`, error);
      attempt++;
      if (attempt >= retries) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, delay));
      // Increase delay for next retry (exponential backoff)
      delay *= 2;
    }
  }
  throw lastError;
};

export function ClientProvider({ children }: { children: React.ReactNode }) {
  const [clients, setClients] = useState<Client[]>([]);
  const [statusFilter, setStatusFilter] = useState<ClientStatus | 'All'>('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<Metrics>(defaultMetrics);
  const navigate = useNavigate();
  const { user, isUserLoaded } = useUser();

  const fetchClients = useCallback(async () => {
    try {
      // Wait for user data to be available before fetching clients
      if (!isUserLoaded) {
        setIsLoading(true);
        return;
      }

      // If there's no authenticated user or agency, clear clients and stop loading
      if (!user || !user.agency_id) {
        setClients([]);
        setMetrics(defaultMetrics);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      // Check for valid session before making the request
      const { data: sessionData } = await safeSupabaseQuery(() => supabase.auth.getSession());
      if (!sessionData.session) {
        navigate('/login');
        return;
      }

      let query = supabase
        .from('clients')
        .select(`
          id,
          name,
          email,
          phone,
          industry,
          status,
          account_owner_id,
          account_owner_name,
          open_tasks,
          tasks_due_this_week,
          pending_daily_tasks,
          completed_daily_tasks,
          tasks_in_progress,
          last_activity_date,
          next_meeting_date,
          google_sheet_url,
          media_domain,
          tags,
          created_at,
          updated_at,
          start_date
        `, { count: 'exact' })
        .order('created_at', { ascending: false });

      // Filter by user's agency_id
      query = query.eq('agency_id', user.agency_id);

      if (statusFilter !== 'All') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await safeSupabaseQuery(() => query);

      if (error) {
        throw error;
      }

      const clientData = data || [];
      
      // Transform data to match the expected Client interface
      const transformedClients = (clientData as ClientRow[]).map((client) => ({
        id: client.id,
        name: client.name,
        status: client.status as ClientStatus,
        email: client.email || undefined,
        phone: client.phone || undefined,
        industry: client.industry || undefined,
        tags: client.tags || [],
        accountOwner: client.account_owner_id ? {
          id: client.account_owner_id,
          name: client.account_owner_name || 'Unknown'
        } : undefined,
        startDate: client.start_date || undefined,
        lastActivityDate: client.last_activity_date || undefined,
        nextMeetingDate: client.next_meeting_date || undefined,
        createdAt: client.created_at ?? '',
        updatedAt: client.updated_at ?? '',
        tasks_in_progress: client.tasks_in_progress || 0,
        tasksDueThisWeek: client.tasks_due_this_week || 0,
        tasksDueToday: client.pending_daily_tasks || 0,
        tasksAwaitingReview: client.completed_daily_tasks || 0
      }));
      
      setClients(transformedClients);

      // Calculate metrics
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const rows = clientData as ClientRow[];
      const activeClients = rows.filter((client) => client.status === 'Active').length;
      const newClientsThisMonth = rows.filter(
        (client) => new Date(client.created_at ?? 0) >= firstDayOfMonth && client.status !== 'Archived'
      ).length;
      const lostClientsThisMonth = rows.filter(
        (client) => client.status === 'Archived' && new Date(client.updated_at ?? 0) >= firstDayOfMonth
      ).length;

      setMetrics({
        activeClients,
        newClientsThisMonth,
        lostClientsThisMonth,
        netChange: newClientsThisMonth - lostClientsThisMonth
      });

    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, navigate, user, isUserLoaded]);

  // Set up real-time subscription
  useEffect(() => {
    let clientsSubscription: RealtimeChannel | null = null;
    // The session check is async, so cleanup can run before setup finishes;
    // without this guard an orphaned subscribed channel survives, and the
    // next effect run would collide with its topic and throw.
    let cancelled = false;

    const setupRealtimeSubscription = async () => {
      // Check for valid session before subscribing
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || cancelled) return;

      // Subscribe to changes on the clients table
      clientsSubscription = supabase
        .channel(uniqueChannelName('clients-changes'))
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'clients'
          },
          () => {
            // Refresh clients data when changes occur
            fetchClients();
          }
        )
        .subscribe();
    };

    setupRealtimeSubscription();

    // Cleanup subscription on unmount
    return () => {
      cancelled = true;
      if (clientsSubscription) {
        supabase.removeChannel(clientsSubscription);
      }
    };
  }, [fetchClients]);

  // Fetch clients on mount
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Pull-to-refresh remounts pages, not this provider
  useEffect(() => onAppRefresh(() => { fetchClients(); }), [fetchClients]);

  const getClientById = useCallback(async (id: string): Promise<Client | undefined> => {
    try {
      setIsLoading(true);

      // Use a more detailed query to fetch the client with all its related data
      const { data, error } = await safeSupabaseQuery(() =>
        supabase
          .from('clients')
          .select(`
            id,
            name,
            email,
            phone,
            industry,
            status,
            account_owner_id,
            account_owner_name,
            open_tasks,
            tasks_due_this_week,
            pending_daily_tasks,
            completed_daily_tasks,
            tasks_in_progress,
            last_activity_date,
            next_meeting_date,
            google_sheet_url,
            notes,
            tags,
            media_domain,
            created_at,
            updated_at,
            start_date
          `)
          .eq('id', id)
          .single()
      );

      if (error) {
        throw error;
      }

      if (!data) {
        return undefined;
      }

      // Fetch client platform IDs
      const { data: platformsData, error: platformsError } = await safeSupabaseQuery(() => 
        supabase
          .from('client_platform_ids')
          .select('*')
          .eq('client_id', id)
      );

      if (platformsError) {
        throw platformsError;
      }

      // Fetch client notes
      const { data: notesData, error: notesError } = await safeSupabaseQuery(() => 
        supabase
          .from('client_notes')
          .select('*')
          .eq('client_id', id)
          .order('created_at', { ascending: false })
      );

      if (notesError) {
        throw notesError;
      }

      // Fetch client files
      const { data: filesData, error: filesError } = await safeSupabaseQuery(() => 
        supabase
          .from('client_files')
          .select('*')
          .eq('client_id', id)
          .order('uploaded_at', { ascending: false })
      );

      if (filesError) {
        throw filesError;
      }

      // Fetch client tasks
      const { data: tasksData, error: tasksError } = await safeSupabaseQuery(() =>
        supabase
          .from('client_tasks')
          .select(`
            id,
            title,
            status,
            due_date,
            assigned_by
          `)
          .eq('client_id', id)
          .eq('archived', false)
          .order('created_at', { ascending: false })
      );

      if (tasksError) {
        throw tasksError;
      }

      // Transform data to match the expected Client interface
      const transformedClient: Client = {
        id: data.id,
        name: data.name,
        status: data.status as ClientStatus,
        email: data.email || undefined,
        phone: data.phone || undefined,
        industry: data.industry || undefined,
        notes: data.notes || undefined,
        tags: data.tags || [],
        accountOwner: data.account_owner_id ? {
          id: data.account_owner_id,
          name: data.account_owner_name || 'Unknown'
        } : undefined,
        startDate: data.start_date || undefined,
        lastActivityDate: data.last_activity_date || undefined,
        nextMeetingDate: data.next_meeting_date || undefined,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        media_domain: data.media_domain || undefined,
        google_sheet_url: data.google_sheet_url || undefined,
        tasks_in_progress: data.tasks_in_progress || 0,
        tasksDueThisWeek: data.tasks_due_this_week || 0,
        tasksDueToday: data.pending_daily_tasks || 0,
        tasksAwaitingReview: data.completed_daily_tasks || 0,
        // Transform related data
        platforms: platformsData?.map((platform: any) => ({
          id: platform.id,
          platform: platform.platform,
          identifier: platform.identifier,
          url: platform.url
        })) || [],
        clientNotes: notesData?.map((note: any) => ({
          id: note.id,
          content: note.content,
          createdAt: note.created_at,
          createdBy: {
            id: note.created_by || 'unknown',
            name: note.created_by_name || 'Unknown User'
          }
        })) || [],
        files: filesData?.map((file: any) => ({
          id: file.id,
          name: file.name,
          type: file.type || 'unknown',
          size: file.size || 0,
          uploadedBy: {
            id: file.uploaded_by || 'unknown',
            name: file.uploaded_by_name || 'Unknown User'
          },
          uploadedAt: file.uploaded_at,
          url: file.url
        })) || [],
        tasks: tasksData?.map((task: any) => ({
          id: task.id,
          title: task.title || 'Untitled Task',
          status: task.status,
          dueDate: task.due_date,
          assignedBy: task.assigned_by
        })) || []
      };

      return transformedClient;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      console.error('Error fetching client by ID:', errorMessage);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addClientTask = useCallback(async (clientId: string, taskData: any) => {
    try {
      // First check for valid session
      const { data: sessionData } = await safeSupabaseQuery(() => supabase.auth.getSession());
      if (!sessionData.session) {
        navigate('/login');
        return { success: false, error: 'No authenticated session found. Please log in.' };
      }

      const { data: user } = await safeSupabaseQuery(() => supabase.auth.getUser());
      if (!user?.user) {
        return { success: false, error: 'No authenticated user found. Please log in.' };
      }


      const { error } = await safeSupabaseQuery(() =>
        supabase
          .from('client_tasks')
          .insert({
            client_id: clientId,
            status: 'Scheduled',
            due_date: taskData.dueDate,
            assigned_by: user.user.id,
            assigned_to: taskData.assignedTo || null,
            title: taskData.title || 'New Task',
            description: taskData.notes || null
          })
          .select('id, title, status, due_date, assigned_by')
          .single()
      );

      if (error) {
        console.error('Error adding client task:', error);
        return { success: false, error: error.message };
      }

      // Refresh clients to update task counts
      await fetchClients();
      
      return { success: true };
    } catch (error) {
      console.error('Error adding client task:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    }
  }, [fetchClients, navigate]);

  const updateClientTaskStatus = useCallback(async (taskId: string, status: string) => {
    try {
      const { error } = await safeSupabaseQuery(() => 
        supabase
          .from('client_tasks')
          .update({ status })
          .eq('id', taskId)
      );

      if (error) throw error;
      
      // Refresh clients to update task counts
      await fetchClients();
      
      return true;
    } catch (error) {
      console.error('Error updating task status:', error);
      return false;
    }
  }, [fetchClients]);

  const addClientPlatformId = useCallback(async (clientId: string, platformData: any) => {
    try {
      const { error } = await safeSupabaseQuery(() => 
        supabase
          .from('client_platform_ids')
          .insert({
            client_id: clientId,
            platform: platformData.platform,
            identifier: platformData.identifier,
            url: platformData.url
          })
      );

      if (error) {
        console.error('Error adding platform ID:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error adding platform ID:', error);
      return false;
    }
  }, []);

  const updateClientTags = useCallback(async (clientId: string, tags: string[]) => {
    try {
      const { error } = await safeSupabaseQuery(() =>
        supabase
          .from('clients')
          .update({ tags })
          .eq('id', clientId)
      );

      if (error) {
        console.error('Error updating tags:', error);
        return false;
      }

      // Update local state
      setClients(clients.map(client =>
        client.id === clientId ? { ...client, tags } : client
      ));

      return true;
    } catch (error) {
      console.error('Error updating tags:', error);
      return false;
    }
  }, [clients]);

  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients;
    
    return clients.filter(client =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.industry?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [clients, searchTerm]);

  return (
    <ClientContext.Provider
      value={{
        clients,
        setClients,
        statusFilter,
        setStatusFilter,
        searchTerm,
        setSearchTerm,
        filteredClients,
        selectedClient,
        setSelectedClient,
        isLoading,
        metrics,
        fetchClients,
        getClientById,
        addClientTask,
        updateClientTaskStatus,
        addClientPlatformId,
        updateClientTags
      }}
    >
      {children}
    </ClientContext.Provider>
  );
}

export function useClients() {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClients must be used within a ClientProvider');
  }
  return context;
}